import { useState, useEffect, useMemo } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { User, Lock, Trash2, Camera, Bell, Globe, Sun, Moon, UserX, ShieldOff } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import ConfirmModal from '@/components/ConfirmModal'

function AvatarPreview({ avatarFile, userAvatar }: { avatarFile: File | null; userAvatar?: string }) {
  const url = useMemo(() => avatarFile ? URL.createObjectURL(avatarFile) : null, [avatarFile])

  useEffect(() => {
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [url])

  return (
    <Avatar className="w-20 h-20">
      <AvatarImage src={url || userAvatar || '/api/placeholder/80/80'} alt="Avatar" />
      <AvatarFallback>?</AvatarFallback>
    </Avatar>
  )
}

export default function Settings() {
  const { t, i18n } = useTranslation()
  const { user, updateUser, logout } = useAuthStore()
  const [activeLang, setActiveLang] = useState(i18n.language || 'tr')
  const queryClient = useQueryClient()
  const [username, setUsername] = useState(user?.username || '')
  const [bio, setBio] = useState(user?.bio || '')
  const [location, setLocation] = useState(user?.location || '')
  const [website, setWebsite] = useState(user?.website || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: user?.notificationSettings?.emailNotifications ?? true,
    commentNotifications: user?.notificationSettings?.commentNotifications ?? true,
    likeNotifications: user?.notificationSettings?.likeNotifications ?? true,
    messageNotifications: user?.notificationSettings?.messageNotifications ?? true,
    followNotifications: user?.notificationSettings?.followNotifications ?? true,
  })
  const [theme, setTheme] = useTheme()
  const [confirmModal, setConfirmModal] = useState<{open: boolean; title: string; description?: string; onConfirm: () => void}>({ open: false, title: '', onConfirm: () => {} })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    document.documentElement.classList.toggle('light', theme === 'light')
    localStorage.setItem('theme', theme)
  }, [theme])

  const updateProfile = useMutation({
    mutationFn: async () => {
      const formData = new FormData()
      formData.append('username', username)
      formData.append('bio', bio)
      formData.append('location', location)
      formData.append('website', website)
      if (avatarFile) formData.append('avatar', avatarFile)
      return api.post('/users/update', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: (res) => {
      updateUser(res.data.user)
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast({ title: t('settings.profileUpdated') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const updatePassword = useMutation({
    mutationFn: () => api.post('/users/update-password', { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      toast({ title: t('settings.passwordUpdated') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const updateNotifications = useMutation({
    mutationFn: () => api.post('/users/update-notifications', notificationSettings),
    onSuccess: (res) => {
      updateUser(res.data.user)
      toast({ title: t('settings.profileUpdated') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const deleteAccount = useMutation({
    mutationFn: () => api.delete('/users/delete'),
    onSuccess: () => {
      logout()
      toast({ title: t('settings.accountDeleted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const askConfirm = (title: string, description: string, onConfirm: () => void) => {
    setConfirmModal({ open: true, title, description, onConfirm })
  }

  const notificationRows = [
    { key: 'emailNotifications' as const, label: t('settings.emailNotifications') },
    { key: 'commentNotifications' as const, label: t('settings.commentNotifications') },
    { key: 'likeNotifications' as const, label: t('settings.likeNotifications') },
    { key: 'messageNotifications' as const, label: t('settings.messageNotifications') },
    { key: 'followNotifications' as const, label: t('settings.followNotifications') },
  ]

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        description={confirmModal.description}
        variant="danger"
        onConfirm={() => { confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, open: false }) }}
        onCancel={() => setConfirmModal({ ...confirmModal, open: false })}
      />

      <h1 className="font-display text-2xl font-bold mb-6">{t('settings.title')}</h1>

      <div className="space-y-6">
        {/* Profile */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">{t('settings.profile')}</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <AvatarPreview avatarFile={avatarFile} userAvatar={user?.avatar} />
                <label className="absolute bottom-0 right-0 p-1.5 rounded-full bg-primary text-white cursor-pointer">
                  <Camera className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setAvatarFile(e.target.files?.[0] || null)} />
                </label>
              </div>
            </div>
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('auth.username')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder={t('profile.bio')} maxLength={500} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none" />
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('profile.location')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder={t('profile.website')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <Button onClick={() => updateProfile.mutate()} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? t('common.saving') : t('settings.updateProfile')}
            </Button>
          </div>
        </motion.div>

        {/* Password */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="p-6 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">{t('settings.password')}</h2>
          </div>
          <div className="space-y-4">
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder={t('settings.currentPassword')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('settings.newPassword')} className="w-full px-4 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50" />
            <Button onClick={() => updatePassword.mutate()} disabled={updatePassword.isPending}>
              {updatePassword.isPending ? t('common.updating') : t('settings.changePassword')}
            </Button>
          </div>
        </motion.div>

        {/* Preferences */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }} className="p-6 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">{t('settings.preferences')}</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.language')}</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    i18n
                      .changeLanguage('tr')
                      .then(() => {
                        setActiveLang('tr')
                        toast({ title: t('settings.languageChanged') })
                      })
                      .catch((err: any) => toast({ title: err?.message || t('common.error'), variant: 'destructive' }))
                  }
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors cursor-pointer active:scale-95 ${activeLang === 'tr' ? 'bg-primary text-white border-primary' : 'bg-secondary border-border hover:bg-secondary/80'}`}
                >
                  {t('settings.languageTurkish')}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    i18n
                      .changeLanguage('en')
                      .then(() => {
                        setActiveLang('en')
                        toast({ title: t('settings.languageChanged') })
                      })
                      .catch((err: any) => toast({ title: err?.message || t('common.error'), variant: 'destructive' }))
                  }
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors cursor-pointer active:scale-95 ${activeLang === 'en' ? 'bg-primary text-white border-primary' : 'bg-secondary border-border hover:bg-secondary/80'}`}
                >
                  {t('settings.languageEnglish')}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('settings.theme')}</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTheme('light')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors cursor-pointer active:scale-95 ${theme === 'light' ? 'bg-primary text-white border-primary' : 'bg-secondary border-border hover:bg-secondary/80'}`}
                >
                  <Sun className="w-4 h-4" /> {t('settings.light')}
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('dark')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors cursor-pointer active:scale-95 ${theme === 'dark' ? 'bg-primary text-white border-primary' : 'bg-secondary border-border hover:bg-secondary/80'}`}
                >
                  <Moon className="w-4 h-4" /> {t('settings.dark')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Notifications */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="p-6 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">{t('settings.notificationSettings')}</h2>
          </div>
          <div className="space-y-3">
            {notificationRows.map(({ key, label }) => (
              <label key={key} className="flex items-center justify-between cursor-pointer">
                <span className="text-sm">{label}</span>
                <input
                  type="checkbox"
                  checked={notificationSettings[key]}
                  onChange={(e) => setNotificationSettings((prev) => ({ ...prev, [key]: e.target.checked }))}
                  className="w-5 h-5 rounded border-border text-primary focus:ring-primary/50"
                />
              </label>
            ))}
            <div className="pt-2">
              <Button onClick={() => updateNotifications.mutate()} disabled={updateNotifications.isPending}>
                {updateNotifications.isPending ? t('common.saving') : t('settings.saveNotifications')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Danger */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="p-6 rounded-2xl bg-card border border-destructive/30">
          <div className="flex items-center gap-2 mb-4">
            <Trash2 className="w-5 h-5 text-destructive" />
            <h2 className="font-semibold text-destructive">{t('settings.dangerZone')}</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{t('settings.deleteWarning')}</p>
          <Button variant="destructive" onClick={() => askConfirm(t('settings.deleteAccount'), t('settings.deleteConfirm'), () => deleteAccount.mutate())}>
            <Trash2 className="w-4 h-4 mr-2" /> {t('settings.deleteAccount')}
          </Button>
        </motion.div>

        {/* F3.UX-7: Blocked users list */}
        <BlockedUsersSection />
      </div>
    </div>
  )
}

// F3.UX-7: Blocked users list
function BlockedUsersSection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['me-blocked'],
    queryFn: () => api.get('/users/me/blocked').then(r => r.data),
  })

  const unblock = useMutation({
    mutationFn: (userId: string) => api.post(`/users/${userId}/unblock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me-blocked'] })
      toast({ title: t('settings.unblocked') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' }),
  })

  const blocked = data?.blocked || []

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="p-6 rounded-2xl bg-card border border-border">
      <div className="flex items-center gap-2 mb-4">
        <UserX className="w-5 h-5 text-primary" />
        <h2 className="font-semibold">{t('settings.blockedUsers')}</h2>
      </div>
      {isLoading ? (
        <div className="h-12 bg-secondary rounded-lg animate-pulse" />
      ) : blocked.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('settings.noBlockedUsers')}</p>
      ) : (
        <ul className="space-y-2">
          {blocked.map((u: any) => (
            <li key={u._id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30">
              <Avatar className="w-9 h-9">
                <AvatarImage src={u.avatar} />
                <AvatarFallback>{u.username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{u.username}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unblock.mutate(u._id)}
                disabled={unblock.isPending}
                aria-label={t('settings.unblockUser', { username: u.username })}
              >
                <ShieldOff className="w-4 h-4 mr-1" /> {t('settings.unblock')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  )
}
